<template>
  <div class="comment-refContainer" ref="refContainer">
    <div class="body-area">
      <div class="spinner-layer" v-if="state.isFetchingData">
        <i class="mr8 fa-3x fas fa-spinner fa-spin"></i>
      </div>
      <ul class="timeline">
        <div
          v-if="elem.data.isChildComment && !parentComment"
          class="component-box no-parent-placeholder mr8 mb8 pd6"
        >
          <h5 class="mb0 mr12">返信元付箋</h5>
          <button class="btn btn-sm btn-secondary" @click="searchParent">
            付箋を取得
          </button>
          <div class="error" v-if="state.showFetchDataError">
            {{fetchDataErrorMessage}}
          </div>
          <div class="spacer"></div>
          <div class="move-buttons mt2">
            <button class="btn btn-primary btn-xs mr4" @click="showPrevious">
              <i class="fas fa-arrow-left"></i>
              前へ
            </button>
            <button class="btn btn-primary btn-xs" @click="showNext">
              次へ
              <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
        <div v-for="(comment, index) in displayedComments" :key="comment.data.id" ref="refCommentElems">
          <div class="component-box mr8 mb8" :class="{ ml18: comment.data.isChildComment }">
            <MapElemInfoComment
              :elem="comment"
              :update-failed="updateFailed"
              @show-image="showImage"
              :show-move-buttons="comment.data.isParentComment"
              @show-previous="showPrevious"
              @show-next="showNext"
              @area-movie-search="doAreaMovieSearch"
              @update="onChildComponentUpdate"
              @confirm-delete="confirmDelete"
              @cancel-edit="$emit('cancel-edit')"
            />
          </div>
          <div v-if="state.collapseNumber && index === 0 && displayedComments.length !== relativeComments.length + 1">
            <button type="button" class="btn btn-lg btn-block btn-secondary mb6" @click="disableCollapse">
              もっと見る（{{ relativeComments.length + 1 - displayedComments.length }}件）
              <i class="fas fa-chevron-down"></i>
            </button>
          </div>
        </div>
      </ul>
      <div v-if="!state.isStartReply" class="mr8 mb6">
        <button type="button" key="reply" class="btn btn-sm btn-primary" @click="startReply">
          <i class="fas fa-reply mr2"></i>返信
        </button>
      </div>
      <div v-else>
        <div class="component-box mr8 mb8" ref="refNewCommentElem">
          <MapElemInfoComment
            :elem="initialCommentForReply"
            :create-failed="createFailed"
            :is-create-single-new-element="true"
            :force-show-cancel-button="true"
            @update="onChildComponentUpdate"
            @cancel-edit="cancelReply"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  reactive,
  computed,
  watch,
  nextTick,
  useTemplateRef,
  onMounted,
} from 'vue';
import type { MapElemInfo, FetchGeoItemsParams, QueryArea } from 'src/models';
import type { GIComment } from 'src/models/geoItem';
import MapElemInfoComment from './MapElemInfoComment.vue';
import type { Comment } from 'src/models/comment';

interface ContainerElement extends MapElemInfo<GIComment> {
  relativeComments: GIComment[];
}

interface MapElemInfoCommentContainerState {
  isStartReply: boolean;
  collapseNumber: number;
  isFetchingData: boolean;
  showFetchDataError: boolean;
}

// 子コンポーネント(MapElemInfoComment)から受け取り、親へそのまま中継するペイロード型
type ShowImageParams = {
  modalTitle: string;
  imageUrls: string[];
  downloadFilenameTemplate: string;
  initialIndex: number;
};
type CommentMutationPayload = { dataName: string; data: Comment };
type ConfirmDeletePayload = { dataName: string; data: Comment; confirmMessage: string };

interface Props {
  elem: ContainerElement;
  createFailed: number;
  updateFailed: number;
  fetchGeoItemFailed: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update', payload: CommentMutationPayload): void;
  (e: 'create', payload: CommentMutationPayload): void;
  (e: 'confirm-delete', payload: ConfirmDeletePayload): void;
  (e: 'cancel-edit'): void;
  (e: 'show-image', payload: ShowImageParams): void;
  (e: 'show-previous-geo-item', payload: ContainerElement): void;
  (e: 'show-next-geo-item', payload: ContainerElement): void;
  (e: 'area-movie-search', payload: QueryArea): void;
  (e: 'fetch-geo-item', payload: FetchGeoItemsParams): void;
}>();

const state = reactive<MapElemInfoCommentContainerState>({
  isStartReply: false,
  collapseNumber: 5,
  isFetchingData: false,
  showFetchDataError: false,
});
const refContainer = useTemplateRef<HTMLElement>('refContainer');

const scrollToTargetComment = (target: HTMLElement) => {
  if (!refContainer.value) return;

  const containerRect = refContainer.value.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const isViewable = targetRect.top >= containerRect.top &&
    targetRect.bottom <= containerRect.top + refContainer.value.clientHeight;
  if (!isViewable) {
    const scrollTop = targetRect.top - containerRect.top;
    const scrollBot = targetRect.bottom - containerRect.bottom;
    if (Math.abs(scrollTop) < Math.abs(scrollBot)) {
      refContainer.value.scrollTop += scrollTop;
    } else {
      refContainer.value.scrollTop += scrollBot;
    }
  }
};
const refCommentElems = useTemplateRef<HTMLElement[]>('refCommentElems');
const displayTargetComment = () => {
  nextTick(() => {
    const targetIdx = displayedComments.value.findIndex(comment => comment.data.id === props.elem.data.id);
    if (targetIdx >= 0 && refCommentElems.value) {
      const targetElem = refCommentElems.value[targetIdx];
      scrollToTargetComment(targetElem);
    }
  });
};
const initDisplay = () => {
  state.isStartReply = false;
  state.collapseNumber = 5;
  state.isFetchingData = false;
  state.showFetchDataError = false;
  displayTargetComment();
};
onMounted(initDisplay);
watch(() => props.elem, initDisplay);
watch(() => props.fetchGeoItemFailed, () => {
  if (props.fetchGeoItemFailed > 0) {
    state.showFetchDataError = true;
  }
});
const relativeComments = computed(() => {
  return props.elem.relativeComments || [];
});
const displayedComments = computed(() => {
  const allComments = [
    props.elem,
    ...relativeComments.value.map(comment => ({
      dataName: props.elem.dataName,
      data: comment,
    })),
  ].sort((a, b) => {
    if (a.data.isParentComment) return -1;
    if (b.data.isParentComment) return 1;

    return a.data.ts && b.data.ts && a.data.ts < b.data.ts ? -1
      : a.data.ts && b.data.ts && a.data.ts > b.data.ts ? 1 : 0;
  });
  if (state.collapseNumber && allComments.length >= state.collapseNumber) {
    return [allComments[0], allComments[allComments.length - 1]];
  }
  return allComments;
});
const parentComment = computed(() => {
  return relativeComments.value.find(e => e.id === props.elem.data.parentId);
});
const initialCommentForReply = computed(() => {
  return {
    dataName: props.elem.dataName,
    data: {
      lat: props.elem.data.location.lat,
      lon: props.elem.data.location.lon,
      parentId: props.elem.data.parentId || props.elem.data.id,
      kpUid: parentComment.value?.kpUid || props.elem.data.kpUid,
    },
  };
});
// methods
const refNewCommentElem = useTemplateRef<HTMLElement>('refNewCommentElem');
const startReply = () => {
  state.isStartReply = true;
  nextTick(() => {
    if (!refNewCommentElem.value) return;

    scrollToTargetComment(refNewCommentElem.value);
  });
};
const onChildComponentUpdate = (obj: CommentMutationPayload) => {
  if (obj.data.id) {
    emit('update', obj);
    return;
  }
  emit('create', obj);
};
const confirmDelete = (obj: ConfirmDeletePayload) => {
  emit('confirm-delete', obj);
};
const cancelReply = () => {
  state.isStartReply = false;
  emit('cancel-edit');
};
const disableCollapse = () => {
  state.collapseNumber = 0;
};
const showImage = (obj: ShowImageParams) => {
  emit('show-image', obj);
};
const showPrevious = () => {
  emit('show-previous-geo-item', props.elem);
};
const showNext = () => {
  emit('show-next-geo-item', props.elem);
};
const doAreaMovieSearch = (queryArea: QueryArea) => {
  emit('area-movie-search', queryArea);
};
const searchParent = () => {
  // 連打防止
  if (state.isFetchingData) return;

  // 親側で再表示する際に、isFetchingDataがfalseに戻る
  state.isFetchingData = true;
  const evtObj: FetchGeoItemsParams = {
    dataName: props.elem.dataName,
    id: props.elem.data.parentId,
  };
  emit('fetch-geo-item', evtObj);
};

const fetchDataErrorMessage = '付箋の閲覧権限がありません。';

</script>

<style lang="scss" scoped>
  .comment-refContainer {
    min-height: 80px;
    max-height: 520px;
    padding: 6px 8px;
    position: relative;
    overflow-x: hidden;
    overflow-y: scroll;
  }
  .timeline {
    position: relative;
    display: block;
    padding-inline-start: 0;
    .component-box {
      background-color: white;
    }
    &:before {
      display: block;
      content: "";
      position: absolute;
      top: 0;
      bottom: -16px;
      left: 10px;
      width: 2px;
      background-color: #a4a5aa;
      z-index: -1;
    }
  }
  .no-parent-placeholder {
    display: flex;
    align-items: center;
    height: 52px;
    .spacer {
      flex-grow: 1;
    }
  }
  .spinner-layer {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 55;
    background-color: rgba(0, 0, 0, 0.4);
    display: flex;
    justify-content: center;
    .fa-spinner {
      align-self: center;
    }
  }
  .error {
    color: red;
    margin-left: 2px;
  }
</style>
